/**
 * Module dependencies.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import { TreeNode } from './file-button';
import { Trash2 } from 'lucide-react';
import { useActionsContent } from './actions-provider';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';

/**
 * `Props` type.
 */

type Props = {
  node: TreeNode;
};

/**
 * Export `RemoveItem` component.
 */

export function RemoveItem({ node }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { success } = useToast();
  const { deleteItem } = useActionsContent();
  const [open, setOpen] = useState(false);

  const handleOk = useCallback(async () => {
    setOpen(false);

    try {
      await deleteItem({ id: node.key });

      success({ message: translate('notes.movedToTrash') });
    } catch (error) {
      // The provider already surfaces the error toast.
    }
  }, [deleteItem, node.key, success, translate]);

  return (
    <>
      <div className={'text-muted-foreground hover:text-destructive'} onClick={() => setOpen(true)}>
        <Trash2 className={'h-4 w-4'} />
      </div>

      <AlertDialog onOpenChange={setOpen} open={open}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('notes.actions.removeItem')}</AlertDialogTitle>

            <AlertDialogDescription>{translate('notes.removeItemDescription')}</AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
              onClick={handleOk}
            >
              {translate('common.actions.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
